/**
 * Utility function to create a delay or pause for a specified amount of time.
 *
 * @param {number} ms - The amount of time in milliseconds to delay.
 * @returns {Promise<void>} - Returns a promise that resolves after the specified delay.
 */
export const delay = async (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Parses an HTML string, wraps it in a div, and returns the div element.
 *
 * @param {string} html - The HTML string to parse.
 * @returns {HTMLDivElement} - A div element containing the parsed HTML.
 */
export const parseHTMLString = (html) => {
  const doc = new DOMParser().parseFromString(html.trim(), "text/html");
  const wrapperDiv = document.createElement("div");

  // Move all child nodes from body into the wrapper div
  Array.from(doc.body.childNodes).forEach((node) => {
    wrapperDiv.appendChild(node);
  });

  return wrapperDiv;
};

/**
 * Utility function to split an array into smaller chunks of a specified size.
 *
 * @template T
 * @param {T[]} array - The array to be chunked.
 * @param {number} size - The size of each chunk.
 * @returns {T[][]} - A new array containing chunks of the original array.
 * Each chunk is an array of the specified size, except possibly the last one.
 */
export const arrayChunk = (array, size) => {
  const chunkedArr = [];
  for (let i = 0; i < array.length; i += size) {
    chunkedArr.push(array.slice(i, i + size));
  }
  return chunkedArr;
};

/**
 * Utility function to generate the Shopify product gid if it's not in gid format.
 *
 * @param {string|number} id - The product ID, either as a number or gid.
 * @returns {string} - Returns the gid for the product in the format gid://shopify/Product/{id}.
 */
export const generateGid = (id) => {
  const gidPattern = /^gid:\/\/shopify\/Product\/\d+$/;

  if (typeof id === "string" && gidPattern.test(id)) {
    return id;
  }

  if (!Number.isNaN(+id)) {
    return `gid://shopify/Product/${id}`;
  }

  return "";
};

/**
 * Utility function to extract the numeric ID from a Shopify GID.
 *
 * @param {string} gid - The GID string in the format gid://shopify/Product/{id}.
 * @returns {string|null} - The numeric ID as a string, or null if format is invalid.
 */
export const extractIdFromGid = (gid) => {
  const match =
    typeof gid === "string" ? gid.match(/gid:\/\/shopify\/\w+\/(\d+)/) : null;
  return match && match[1] ? match[1] : null;
};

/**
 * Attempts to parse JSON and returns the fallback if there's an error.
 *
 * @template T
 * @param {string|null} json - The JSON string to parse.
 * @param {T|null} [fallback=null] - The fallback value if parsing fails.
 * @returns {T|null} - Parsed value if valid JSON, otherwise the fallback.
 */
export const safeParseJSON = (json, fallback = null) => {
  if (!json) {
    return fallback;
  }

  try {
    return /** @type {T} */ (JSON.parse(json));
  } catch {
    return fallback;
  }
};

/**
 * Generates srcset and sizes attributes for a Shopify CDN image.
 *
 * @param {string} fullUrl - The full Shopify CDN image URL (may include width and version).
 * @param {number[]} widths - An array of desired widths for the srcset.
 * @param {string} [defaultWidth='500'] - Default width to use for `src` attribute.
 * @param {string} [sizes] - Optional sizes attribute string.
 * @returns {{ src: string; srcset: string; sizes?: string }}
 */
export const generateSrcSet = (
  fullUrl,
  widths,
  defaultWidth = "500",
  sizes
) => {
  const url = new URL(fullUrl, window.location.origin);

  // Clean width param but preserve others like "v"
  url.searchParams.delete("width");

  const baseUrl = url.origin + url.pathname;
  const preservedParams = url.searchParams.toString();
  const baseQuery = preservedParams ? `?${preservedParams}` : "";
  const connector = preservedParams ? "&" : "?";

  const srcset = widths
    .map((w) => `${baseUrl}${baseQuery}${connector}width=${w} ${w}w`)
    .join(",");

  const src = `${baseUrl}${baseQuery}${connector}width=${defaultWidth}`;
  if (sizes) {
    return { src, srcset, sizes };
  }

  return { src, srcset };
};

/**
 * Parses a price string and extracts the numeric value.
 *
 * - Removes any currency symbols or non-numeric characters.
 * - Supports formats like "$10", "CA$10.00", "10,00 €", "USD 10.00", etc.
 * - Converts comma to dot for decimal handling.
 * - Returns a float or `null` if parsing fails.
 *
 * @param {string} priceString - The price string to parse.
 * @returns {number|null} The numeric value of the price, or null if invalid.
 */
export const parsePrice = (priceString) => {
  const match = priceString.replace(/[^\d.,-]/g, "").replace(",", ".");
  const value = parseFloat(match);
  return isNaN(value) ? null : value;
};

/**
 * Formats a price amount into a locale-specific currency string.
 *
 * - Removes trailing zeros from the formatted price.
 * - Rounds to 2 decimal places if the amount is not an integer.
 * - Uses the active currency from Shopify.
 *
 * @param {string} currency - The currency code to use for formatting.
 * @param {number|string|null} amount - The price amount to format.
 * @returns {string} The formatted price string, original value if unparseable, or empty string if formatting fails.
 */
export const formatPrice = (currency, amount) => {
  // Handle null/undefined early
  if (amount === null || amount === undefined) {
    return String(amount ?? '');
  }

  let num;

  // If already a number, use it directly
  if (typeof amount === "number" && !isNaN(amount)) {
    num = amount;
  } else {
    // Try to parse as price string
    num = parsePrice(String(amount));

    // If parsing failed, return original value
    if (num === null) {
      return String(amount ?? '');;
    }
  }
  const template = window.shopifyVariables?.currencyFormat || '%f';
  const activeCurrency = currency || window.Shopify?.currency?.active || 'USD';

  try {
    // Want to get the default currency format but will ignore the currency codes and symbols this produces
    // Forcing en-US locale for consistency with PDP/cart price formatting
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: activeCurrency,
      currencyDisplay: 'code',
    });

    // Get just the number
    const parts = formatter.formatToParts(num);
    const numericString = parts
      .filter(p => ['integer', 'group', 'decimal', 'fraction', 'minusSign'].includes(p.type))
      .map(p => p.value)
      .join('');

    // Put the number back into currencyFormat template
    return template.replace('%f', numericString);

  } catch (error) {
    console.warn("Error formatting price:", error);
    return "";
  }
};


/**
 * Checks if a given breakpoint is considered mobile.
 * @param {number} [mobileMax=989] - Max mobile width threshold in pixels.
 * @returns {boolean} - Returns true if mobile, false otherwise.
 */
export const isMobileBreakpoint = (mobileMax = 989) => {
  const currentWidth = window.innerWidth;
  return currentWidth < mobileMax;
};

// Bundle specific functions for global fallback
const Utilities = {
  formatPrice,
  isMobileBreakpoint
};

if (typeof window !== "undefined") {
  window.utilities = Object.assign(window.utilities || {}, Utilities);
  window.shopifyVariables = window.shopifyVariables || {};
}
